import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFilter } from "@chakra-ui/react";

import { usePortalContext } from "@/hooks/usePortalContext";
import {
  useTRPC,
  type RentalsMerchantPortalRouterOutputs,
} from "@/trpc/client";

import type { OrgBillFiltersType } from "../types/filters.type";

type OrgBilling = RentalsMerchantPortalRouterOutputs["merchantBills"]["org"];
type OrgBillingMerchant = OrgBilling["merchants"][number];

export type OrgBillRow = OrgBillingMerchant["bills"][number] & {
  merchantId: string;
  merchant: string;
};

const STALE_TIME = 5 * 60_000;
const REFETCH_INTERVAL = 120_000;

export const useOrgBills = (filters: OrgBillFiltersType) => {
  const trpc = useTRPC();
  const { context } = usePortalContext();
  const { contains } = useFilter({ sensitivity: "base" });
  const orgId = context?.type === "org" ? context.id : undefined;

  const query = useQuery({
    ...trpc.merchantBills.org.queryOptions({ rentalsOrgId: orgId ?? "" }),
    enabled: !!orgId,
    staleTime: STALE_TIME,
    refetchInterval: REFETCH_INTERVAL,
  });

  const allRows = useMemo<OrgBillRow[]>(
    () =>
      (query.data?.merchants ?? []).flatMap((merchant) =>
        merchant.bills.map((bill) => ({
          ...bill,
          merchantId: merchant.id,
          merchant: merchant.displayName,
        }))
      ),
    [query.data]
  );

  const rows = useMemo(() => {
    const byStatus =
      filters.status === "Pagado"
        ? allRows.filter((row) => row.paid)
        : filters.status === "Sin pagar"
        ? allRows.filter((row) => !row.paid)
        : allRows;

    return filters.merchant
      ? byStatus.filter((row) => contains(row.merchant, filters.merchant ?? ""))
      : byStatus;
  }, [allRows, filters.status, filters.merchant, contains]);

  return {
    rows,
    organization: query.data?.organization,
    totalOutstanding: query.data?.total.outstanding ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    errorCode: query.error?.data?.code,
    refetch: query.refetch,
  };
};
